import { handleActions } from 'redux-actions';
import { LOCATION_CHANGE, replace } from 'react-router-redux';
import { getFormValues } from 'redux-form';

import moment from 'moment';
import axios from 'axios';
import cloneDeep from 'lodash/cloneDeep';
import range from 'lodash/range';
import isEmpty from 'lodash/isEmpty';
import forEach from 'lodash/forEach';
import qs from 'qs';

import http from '../../shared/http';
import endpoints from '../../../config/endpoints';
import intl from '../../shared/intl';
import checkPermissions from '../../shared/check-permissions';
import csvExport from '../../shared/csvExport';
import createTable from '../components/table';
import createFilters from '../../shared/filters';
import {
	workingGeoFilter, utmTagFilters, dashboardVisitActionFilter, dashboardReferersFilter
} from '../../shared/filters/list';
import createSwitchControlMulti from '../../shared/switch-control/multi';
import { default as createCalendar, getDateRangeFromCalendar } from '../../shared/calendar';
import { enum as affiliateRanks } from '../../../../../../enums/affiliate-ranks';
import { enum as offerStatuses } from '../../../../../../enums/offer-statuses';

import { actions as rootActions } from '../../root';

const isEverad = process.env.CPA_ENV === 'everad';

const table = createTable({
	namespace: 'affiliatesList',
	stateNodeName: 'table',
	configureSort: {
		type: 'remote',
		resolverSortFn: request
	}
});

const months = moment.months().map((month, i) => ({
	value: i + 1,
	label: month
}));

const filters = createFilters({
	namespace: 'affiliatesList',
	stateNodeName: 'filters',
	queryBuildFunc: () => '',
	listOfFilters: [
		{
			name: 'affiliate_ids',
			title: 'Affiliates',
			placeholder: 'Add affiliates',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: true,
			options: {
				type: 'dynamic',
				source: endpoints.LOOKUPS.AFFILIATES
			}
		},
		{
			name: 'referer_domains',
			title: 'Referer domains',
			placeholder: 'Add referer domains',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: true,
			options: {
				type: 'dynamic',
				source: endpoints.LOOKUPS.REFERER_DOMAINS
			}
		},
		{
			name: 'lead_sources',
			title: 'Lead sources',
			placeholder: 'Add lead sources',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: true,
			options: {
				type: 'dynamic',
				source: endpoints.LOOKUPS.LEAD_SOURCES
			}
		},
		{
			name: 'manager_ids',
			title: 'Managers',
			placeholder: 'Add managers',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: true,
			options: {
				type: 'dynamic',
				source: endpoints.LOOKUPS.ADMINS,
				params: isEverad ? 'only_managers=true&limit=100' : null
			}
		},
		{
			name: 'cpa',
			title: 'CPA',
			placeholder: 'Add CPA',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: true,
			options: {
				type: 'provided',
				source: [
					{ value: 'everad', label: 'Everad' },
					{ value: 'rocket', label: 'Rocket' }
				]
			}
		},
		{
			name: 'internal_status',
			title: isEverad ? 'Statuses' : 'Internal statuses',
			placeholder: isEverad ? 'Add statuses' : 'Add internal statuses',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: true,
			options: {
				type: 'static',
				source: 'root.lists.affiliate-internal-statuses'
			}
		},
		{
			name: 'levels',
			title: isEverad ? 'Levels' : 'Account statuses',
			placeholder: isEverad ? 'Add levels' : 'Add account statuses',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: true,
			options: {
				type: 'static',
				source: 'root.lists.affiliate-levels'
			}
		},
		{
			name: 'internal_rank',
			title: isEverad ? 'Ranks' : 'Internal ranks',
			placeholder: isEverad ? 'Add ranks' : 'Add internal ranks',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: true,
			options: {
				type: 'static',
				source: 'root.lists.affiliate-internal-ranks'
			}
		},
		{
			name: 'language',
			title: 'Languages',
			placeholder: 'Add languages',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: true,
			options: {
				type: 'static',
				source: 'root.lists.languages'
			}
		},
		{
			name: 'country',
			title: 'Affiliate country',
			placeholder: 'Add affiliate country',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: true,
			options: {
				type: 'dynamic',
				source: endpoints.LOOKUPS.LOCATIONS
			}
		},
		{
			name: 'city',
			title: 'Affiliate city',
			placeholder: 'Add affiliate city',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: true,
			options: {
				type: 'dynamic',
				source: endpoints.LOOKUPS.CITIES
			}
		},
		{
			name: 'tags',
			title: 'Tags',
			placeholder: 'Add affiliate\'s tag',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: false,
			options: {
				type: 'dynamic',
				source: endpoints.LOOKUPS.AFFILIATE_TAGS
			}
		},
		{
			name: 'birthday',
			title: 'Birthday',
			placeholder: 'Add month',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: true,
			options: {
				type: 'provided',
				source: months
			}
		},
		{
			name: 'offers',
			title: 'Available offer',
			placeholder: 'Add offer',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: true,
			options: {
				type: 'dynamic',
				source: endpoints.LOOKUPS.OFFERS,
				params: qs.stringify({ statuses: [offerStatuses.active, offerStatuses.hidden, offerStatuses.stopped] })
			}
		},
		{
			name: 'is_online',
			title: 'Online',
			placeholder: 'Is online?',
			value: [],
			multi: false,
			shouldInverse: false,
			shouldShowInverseControl: false,
			options: {
				type: 'provided',
				source: [
					{ value: 'yes', label: 'Yes' },
					{ value: 'no', label: 'No' }
				]
			}
		},
		...(!isEverad ? [workingGeoFilter, dashboardReferersFilter, dashboardVisitActionFilter, ...utmTagFilters] : [])
	]
});

const calendar = createCalendar({
	namespace: 'affiliatesList',
	stateNodeName: 'calendar',
	defaultValue: {
		start: moment('2014-03-31').format('YYYY-MM-DD'),
		end: moment().format('YYYY-MM-DD')
	}
});

const statusSwitchControlMultiDefaultValues = [
	{ name: 'new', placeholder: 'New', active: false, className: 'bullet-one' },
	{ name: 'active', placeholder: 'Active', active: false, className: 'bullet-two' },
	{ name: 'prospect', placeholder: 'Prospect', active: false, className: 'bullet-three' },
	{ name: 'inactive', placeholder: 'Inactive', active: false, className: 'bullet-three' },
	{ name: 'archived', placeholder: 'Archived', active: false, className: 'bullet-four' }
];

const rankSwitchControlMultiDefaultValues = affiliateRanks.map((rank) => ({
	name: rank.id,
	placeholder: rank.name,
	active: false
}));

const switchControlMultiDefaultValues = isEverad
	? statusSwitchControlMultiDefaultValues
	: rankSwitchControlMultiDefaultValues;

const switchControlMulti = createSwitchControlMulti({
	namespace: 'affiliatesList',
	stateNodeName: 'switchControlMulti',
	defaultValues: [{ name: 'all', placeholder: 'All', active: true }, ...switchControlMultiDefaultValues]
});

// actions types
const TOGGLE_PROGRESS_BAR = 'affiliates/toggle-progress-bar';
const FETCH_DATA_SUCCESS = 'affiliates/fetch-data-success';
const GOTO_PAGE_SUCCESS = 'affiliates/goto-page-success';
const LOGIN_WITH_AFFILIATE_SUCCESS = 'affiliates/login-with-affiliate-success';

const APPLY = 'affiliates/apply';
const CLEAR = 'affiliates/clear';

const SET_SEARCH_QUERY = 'affiliates/set-search-query';

const REQUEST_ERROR = 'affiliates/request-error';

// permissions
export const allowedPermissions = {
	view: 'affiliates_list',
	download: 'affiliates_list_download',
	edit: 'affiliates_update',
	login: 'affiliates_login',
	viewIds: 'affiliates_ids_list',
	view_statistics: 'analytics_view'
};

// initial state
const initialState = {
	online: 0,
	data: null,
	showProgressBar: false,
	table: table.initialState,
	filters: filters.initialState,
	calendar: calendar.initialState,
	search: '',
	pagination: {
		currentPage: 1,
		perPage: 100,
		total: null
	},
	switchControlMulti: switchControlMulti.initialState,
	error: false
};

// actions
export const actions = Object.assign(
	{
		fetchData: props => (dispatch, getState) => {
			const externalQuery = (props.location && props.location.query && qs.parse(props.location.query)) || {};
			const savedFilters = filters.selectors.getSavedFilters(getState(), 'affiliatesList');

			if (isEmpty(externalQuery) && savedFilters.entity && savedFilters.entity.filters.length) {
				return dispatch(replace(`affiliates/?${qs.stringify(savedFilters)}`));
			}

			if (document.isLoading) {
				return;
			}

			document.isLoading = true;

			return dispatch(request(getState, externalQuery)).then(() => {
				document.isLoading = false;
				if (externalQuery.entity) {
					const filtersList = externalQuery.entity.filters || [];
					dispatch(filters.actions.setFiltersFromExternalQuery(filtersList));
				}
			});
		},

		apply: () => (dispatch, getState) => {
			const searchForm = getFormValues('affiliatesSearchForm')(getState());
			const search = searchForm && searchForm.search;
			dispatch({
				type: APPLY,
				payload: search
			});
			dispatch(request(getState));
		},
		download: () => (dispatch, getState) => {
			const { rows, headers } = selectors.getTable(getState());

			const columns = rows.map(entity => headers.map(h => entity[h.index] || '-'));
			const rowsForExport = [].concat([headers.map(h => h.title)], columns);

			csvExport('Affiliates', rowsForExport);
		},
		export: () => async (dispatch, getState) => {
			const { filters: { selectedFilters } } = getState().affiliatesList;

			const managersFilter = selectedFilters.find((filter) => filter.name === 'manager_ids');
			const geosFilter = selectedFilters.find((filter) => filter.name === 'geos');

			const managers = !managersFilter ? [] : managersFilter.value.map((v) => v.value);
			const geos = !geosFilter ? [] : geosFilter.value.map((v) => v.value);

			const filename = `Выгрузка-${geosFilter.value.map((v) => v.label)}${!managers.length ? '' : `+${managers}`}.csv`;

			try {
				dispatch(rootActions.spinnerToggle());

				const response = await axios({
					url: `${endpoints.AFFILIATES.BASE}/export?${qs.stringify({ managers, geos })}`,
					method: 'GET',
					responseType: 'blob'
				});

				const link = document.createElement('a');

				link.href = window.URL.createObjectURL(new Blob([response.data]));
				link.setAttribute('download', filename);
				document.body.appendChild(link);
				link.click();
			} finally {
				dispatch(rootActions.spinnerToggle());
			}
		},

		search: () => (dispatch, getState) => {
			dispatch(actions.setSearchQuery());
			dispatch(request(getState, dispatch));
		},

		setSearchQuery: () => (dispatch, getState) => {
			const searchForm = getFormValues('affiliatesSearchForm')(getState());
			const search = searchForm && searchForm.search || '';

			dispatch({
				type: SET_SEARCH_QUERY,
				payload: search
			});
		},

		clear: () => dispatch =>  {
			dispatch(actions.toggleProgressBar());
			dispatch(replace('affiliates/'));
			setTimeout(() => dispatch({ type: CLEAR }));
		},

		toggleProgressBar: () => ({
			type: TOGGLE_PROGRESS_BAR
		}),

		loginWithAffiliate: id => dispatch => {
			const isNewDashboard = !isEverad && confirm('Перейти в новый дашборд?');

			const pageData = {
				pathname: window.location.pathname,
				title: 'List of affiliates'
			};
			const request = http({
				url: endpoints.AFFILIATES.LOGIN.replace('{id}', id),
				method: 'post',
				actions: [LOGIN_WITH_AFFILIATE_SUCCESS, REQUEST_ERROR],
				data: {
					isNewDashboard,
					source_page: pageData
				}
			});
			request(dispatch).then(action => {
				if (action.type === LOGIN_WITH_AFFILIATE_SUCCESS) {
					window.location.replace(action.payload.data.url);
				}
			});
		},

		gotoPage: page => (dispatch, getState) => {
			dispatch(actions.toggleProgressBar());

			dispatch({
				type: GOTO_PAGE_SUCCESS,
				payload: page
			});

			request(getState)(dispatch);
		}
	},
	table.actions,
	filters.actions,
	calendar.actions,
	switchControlMulti.actions
);

// reducers
const reducers = Object.assign(
	{
		[LOCATION_CHANGE]: (state, action) => {
			if (action.payload.pathname.indexOf('affiliates/') !== -1) {
				return {
					...state,
					pagination: {
						...state.pagination,
						currentPage: initialState.pagination.currentPage
					},
					online: 0,
					data: null
				};
			}
			return initialState;
		},
		[FETCH_DATA_SUCCESS]: (state, action) => {
			const { online, rows: data, total } = action.payload.data;

			return {
				...state,
				online,
				data,
				pagination: {
					...state.pagination,
					total
				},
				showProgressBar: false,
				error: false
			};
		},
		[APPLY]: (state, action) => ({
			...state,
			pagination: initialState.pagination,
			search: action.payload, showProgressBar: true
		}),
		[CLEAR]: state => initialState,
		[GOTO_PAGE_SUCCESS]: (state, action) => ({
			...state,
			pagination: {
				...state.pagination,
				currentPage: action.payload
			}
		}),
		[REQUEST_ERROR]: (state, action) => ({ ...state, error: true, showProgressBar: false }),
		[TOGGLE_PROGRESS_BAR]: state => ({ ...state, showProgressBar: true }),
		[SET_SEARCH_QUERY]: (state, action) => ({ ...state, search: action.payload })
	},
	table.reducers,
	filters.reducers,
	calendar.reducers,
	switchControlMulti.reducers
);

export default handleActions(reducers, cloneDeep(initialState));

// selectors
export const selectors = {
	getOnlineCount: state => state.affiliatesList.online,
	getData: state => state.affiliatesList.data,
	getAvailableCurrencies: state => state.root.lists['available-currencies'].map(curr => curr.id),
	getTable: table.selectors.getTable,
	getFilters: state => filters.selectors.getFilters(
		state => state.affiliatesList.filters,
		state => state
	)(state),
	getCalendar: state => calendar.selectors.getCalendar(state => state.affiliatesList.calendar, state => state)(state),
	getSearchData: state => ({ search: state.affiliatesList.search }),
	getPagination: state => {
		const { perPage, total, currentPage } = state.affiliatesList.pagination;
		return {
			pages: range(Math.ceil(total / perPage)),
			currentPage,
			total
		};
	},
	getSwitchControlMultiItems: state => switchControlMulti.selectors.getControls(state),
	getCan: state => checkPermissions(state, allowedPermissions),
	getIntl: state => intl(state),
	getProgressBar: state => state.affiliatesList.showProgressBar,
	getError: state => state.affiliatesList.error
};

// functions

function request(getState, externalQuery) {
	const {
		search, calendar, switchControlMulti,
		filters: { selectedFilters },
		pagination: { perPage, currentPage },
		table: { sortByColumn }
	} = getState().affiliatesList;
	const activeQuickFilters = switchControlMulti.filters.filter(f => f.active).map(f => f.name);

	const limit = perPage;
	const offset = (currentPage - 1) * perPage;

	let filters = {};
	const inverseFilters = {};

	if (selectedFilters && !isEmpty(selectedFilters)) {
		selectedFilters.forEach(filter => {
			const values = filter.value.map(f => f.value);
			if (values.length) {
				const selectedFilterValues = filter.shouldInverse ? inverseFilters : filters;
				selectedFilterValues[filter.name] = values;
			}
		});
	}

	if (externalQuery && externalQuery.query && externalQuery.query.filters) {

		externalQuery.entity.filters && externalQuery.entity.filters.forEach(filter => {
			if (filter.is_inverted && JSON.parse(filter.is_inverted)) {
				inverseFilters[filter.name] = filter.values.map(f => f.value);
			}
		});

		externalQuery.query.filters && forEach(externalQuery.query.filters, (value, key) => {
			if (inverseFilters[key]) {
				return;
			}
			filters[key] = value;
		});
	}

	if (!isEverad && activeQuickFilters.length && activeQuickFilters[0] !== 'all') {
		filters.rank = activeQuickFilters;
	}

	const query = qs.stringify({
		offset,
		limit,
		filters: filters,
		inverse_filters: inverseFilters,
		date_range: getDateRangeFromCalendar(calendar),
		search,
		sort: [sortByColumn.index, sortByColumn.direction],
		quick_filters: isEverad && activeQuickFilters.length ? activeQuickFilters : null
	});

	return http({
		url: `${endpoints.AFFILIATES.BASE}?${query}`,
		method: 'get',
		actions: [FETCH_DATA_SUCCESS, REQUEST_ERROR]
	});
}



// WEBPACK FOOTER //
// ./frontend/admin-dashboard/src/modules/affiliates/list/index.js